import React from 'react';
import styled, { css } from 'styled-components';
import { TOOLTIP_DIR_TYPE_VALUE } from '@typings/common/tooltipDirType';
import { motion } from 'framer-motion';
import Color from '@styles/Color';
import { scrollbarHide } from '@styles/variants/scrollbar';

const Content = styled(motion.div) <{
  direction?: keyof typeof dircetionMap;
  noArrow?: boolean;
  $maxWidth?: string;
}>`
  position: absolute;
  z-index: var(--tooltipZIndex, 10000);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 6px;
  background-color: ${Color.GRAY_9};
  color: ${Color.WHITE};
  font-size: 12px;
  line-height: 1.4;
  text-align: left;
  overflow-y: hidden;

  ${({ $maxWidth }) =>
    $maxWidth
      ? `
    max-width: ${$maxWidth};
    width: max-content;
    white-space: normal;
    overflow-y: auto;
    img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
      display: block;
      border-radius: 4px;
    }
  `
      : `
    white-space: nowrap;
    width: max-content;
  `}

  .ql-editor {
    overflow-y: hidden;
  }
  /* 화살표 */
  &::before {
    content: '';
    display: ${({ noArrow }) => (noArrow ? 'none' : 'block')};
    left: 50%;
    border: solid transparent;
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-width: 6px;
    margin-left: calc(6px * -1);
  }

  ${({ direction }) => direction && dircetionMap[direction]}
  ${scrollbarHide}
`;

const dircetionMap = {
  top: css`
    bottom: calc(100% + 8px);

    /* 화살표 */
    &::before {
      top: 100%;
      border-top-color: ${Color.GRAY_9};
    }
  `,
  left: css`
    left: auto;
    right: calc(100% + 8px);
    top: 50%;

    /* 화살표 */
    &::before {
      left: auto;
      right: calc(6px * -2);
      top: 50%;
      transform: translateX(0) translateY(-50%);
      border-left-color: ${Color.GRAY_9};
    }
  `,
  right: css`
    left: calc(100% + 8px);
    top: 50%;

    /* 화살표 */
    &::before {
      left: calc(6px * -1);
      top: 50%;
      transform: translateX(0) translateY(-50%);
      border-right-color: ${Color.GRAY_9};
    }
  `,
  bottom: css`
    top: calc(100% + 8px);

    /* 화살표 */
    &::before {
      bottom: 100%;
      border-bottom-color: ${Color.GRAY_9};
    }
  `,
  leftBottom: css`
    left: auto;
    right: -100px;
    top: 0;
  `,
};

interface IdirTransform {
  top: { x?: string; y?: string };
  left: { x?: string; y?: string };
  right: { x?: string; y?: string };
  bottom: { x?: string; y?: string };
  leftBottom: { x?: string; y?: string };
}

const directionTransformMap: IdirTransform = {
  top: { x: '-50%', y: '0px' },
  left: { x: '0', y: '-50%' },
  right: { x: '0', y: '-50%' },
  bottom: { x: '-50%', y: '0px' },
  leftBottom: { x: '0', y: '0' },
};

type Props = {
  direction?: TOOLTIP_DIR_TYPE_VALUE;
  children?: JSX.Element | any;
  visible?: boolean;
  position?: { x: number; y: number };
  noArrow?: boolean;
  style?: React.CSSProperties;
  maxWidth?: string;
};
function TooltipContents({
  direction = 'top',
  children,
  visible,
  position,
  noArrow = false,
  style,
  maxWidth,
}: Props) {
  if (!visible) {
    return null;
  }

  // 유효한 숫자인지 확인하고 NaN이 아닐 경우에만 사용
  const top = position && !isNaN(position.y) ? position.y : 0;
  const left = position && !isNaN(position.x) ? position.x : 0;

  const variants = {
    hidden: {
      opacity: 0,
      x: directionTransformMap[direction].x,
      y: `calc(${directionTransformMap[direction].y} - 8px)`,
    },
    show: {
      opacity: 1,
      x: directionTransformMap[direction].x,
      y: directionTransformMap[direction].y,
    },
  };

  return (
    <Content
      className="tooltip"
      initial="hidden"
      animate="show"
      exit="hidden"
      variants={variants}
      transition={{ delay: 0 }}
      direction={direction}
      $maxWidth={maxWidth}
      style={{
        ...(position ? { top: top, left: left + 30 } : {}),
        ...style,
      }}
      noArrow={noArrow}
    >
      {typeof children === 'string' ? (
        <div
          className="ql-editor"
          style={{ width: '100%' }}
          dangerouslySetInnerHTML={{
            __html: children || '',
          }}
        />
      ) : (
        <>{children}</>
      )}
    </Content>
  );
}

export default TooltipContents;
